import { useDroppable } from '@dnd-kit/core';

import type { IssueCard } from '@tasks/contracts';

import { Button } from '../../components/Button';
import { IconButton } from '../../components/IconButton';
import { Dropdown } from '../../components/Dropdown';
import { formatDateRange, formatPoints } from '../../lib/format';
import { sprintStateLabels } from '../../i18n/enums';
import { cn } from '../../lib/cn';
import { Ellipsis, Flag, Play, Plus, Rocket } from 'lucide-react';
import type { SprintGroupView } from '../../lib/types';
import { BacklogIssueRow } from './BacklogIssueRow';
import styles from './SprintSection.module.css';

export type SprintSectionProps = {
  readonly sprint: SprintGroupView;
  readonly canManage: boolean;
  readonly isSelected: (issueId: string) => boolean;
  readonly onOpenIssue: (key: string) => void;
  readonly onToggleSelect: (issueId: string, event: { readonly shiftKey: boolean; readonly metaKey: boolean }) => void;
  readonly onStart: () => void;
  readonly onComplete: () => void;
  readonly onDelete: () => void;
  readonly onEdit: () => void;
  readonly onCreateIssue: (sprintId: string) => void;
};

/** Блок спринта: сводка (points, задач, выполнено) и кнопки старта/завершения. */
export function SprintSection({
  sprint,
  canManage,
  isSelected,
  onOpenIssue,
  onToggleSelect,
  onStart,
  onComplete,
  onDelete,
  onEdit,
  onCreateIssue,
}: SprintSectionProps) {
  const { sprint: sprintRef, goal, issues, totalPoints, doneCount, completedAt } = sprint;
  const { setNodeRef, isOver } = useDroppable({
    id: `sprint:${sprintRef.id}`,
    data: { sprintId: sprintRef.id },
  });

  return (
    <section
      ref={setNodeRef}
      className={cn(styles['section'], isOver && styles['over'])}
      aria-label={`Спринт ${sprintRef.name}`}
    >
      <header className={styles['header']}>
        <span className={styles['name']}>{sprintRef.name}</span>
        <span className={cn(styles['state'], styles[sprintRef.state])}>{sprintStateLabels[sprintRef.state]}</span>
        <span className={styles['dates']}>
          {completedAt ? `завершён ${completedAt.slice(0, 10)}` : formatDateRange(sprintRef.startDate, sprintRef.endDate)}
        </span>
        <span className={styles['summary']}>
          {formatPoints(totalPoints)} sp · {doneCount}/{issues.length}
        </span>

        <span className={styles['spacer']} />

        {canManage && sprintRef.state === 'future' ? (
          <Button size="sm" variant="primary" icon={<Play size={14} />} onClick={onStart}>
            Начать
          </Button>
        ) : null}
        {canManage && sprintRef.state === 'active' ? (
          <Button size="sm" variant="primary" icon={<Rocket size={14} />} onClick={onComplete}>
            Завершить
          </Button>
        ) : null}
        {canManage ? (
          <Dropdown
            align="end"
            trigger={({ toggle }) => (
              <IconButton size="sm" label="Действия со спринтом" icon={<Ellipsis size={16} />} onClick={toggle} />
            )}
            items={[
              { id: 'edit', label: 'Изменить', onSelect: onEdit },
              { id: 'delete', label: 'Удалить', danger: true, onSelect: onDelete },
            ]}
          />
        ) : null}
      </header>

      {goal ? (
        <p className={styles['goal']}>
          <Flag aria-hidden size={12} /> {goal}
        </p>
      ) : null}

      {issues.length === 0 ? (
        <p className={styles['empty']}>В спринте нет задач — перетащите их из бэклога.</p>
      ) : (
        <ul className={styles['list']}>
          {issues.map((issue: IssueCard) => (
            <li key={issue.id}>
              <BacklogIssueRow
                issue={issue}
                isSelected={isSelected(issue.id)}
                onOpen={onOpenIssue}
                onToggleSelect={onToggleSelect}
              />
            </li>
          ))}
        </ul>
      )}

      {canManage ? (
        <button type="button" className={styles['addIssue']} onClick={() => onCreateIssue(sprintRef.id)}>
          <Plus aria-hidden size={14} /> Создать задачу
        </button>
      ) : null}
    </section>
  );
}
